/**
 * Employee Management Component
 *
 * Lists cleared employees and anyone with hire history (incl. rehire in progress).
 */
import React, { useCallback, useEffect, useState } from 'react';
import EmpDataTable from './empDataTable';
import NewRegistrationModal from './newRegistration/NewRegistrationModal';
import ManageEmployeeModal from './ManageEmployeeModal';
import { fetchEmployeeManagementList } from '../../../services/employeeManagementApi';
import './empManagement.css';
import addNewIcon from '../../../assests/icons/addNew_icon.png';
import filterIcon from '../../../assests/icons/filter_icon.png';

export type HireLifecycle = 'active' | 'resigned' | 'rehire_in_progress';

export interface Employee {
  id: string;
  empId: string;
  name: string;
  registrationDate: string;
  birthDate: string;
  email: string;
  /** Current empaccount invitation (may be new rehire invite). */
  invitationId?: string;
  /** Last finalized archive invitation — use when opening previous records. */
  archiveInvitationId?: string;
  gender?: string;
  finalizedAt?: string;
  needsFinalize?: boolean;
  status?: string;
  hireLifecycle?: HireLifecycle;
}

export interface EmpManagementProps {
  /** Callback when employee row is clicked (Phase B: archive viewer) */
  onEmployeeClick?: (employee: Employee) => void;
  /** Callback when new registration button is clicked */
  onNewRegistration?: () => void;
  /** After prepare-rehire: navigate to create-invitation with this person */
  onStartRehire?: (participant: { email: string; name: string }) => void;
}

function normalizeLifecycle(raw: string | undefined): HireLifecycle {
  if (raw === 'resigned' || raw === 'rehire_in_progress') return raw;
  return 'active';
}

const EmpManagement: React.FC<EmpManagementProps> = ({
  onEmployeeClick,
  onNewRegistration,
  onStartRehire,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showNewRegistrationModal, setShowNewRegistrationModal] = useState(false);
  const [manageEmployee, setManageEmployee] = useState<Employee | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    period: '',
    gender: '',
    lifecycle: '' as '' | HireLifecycle,
  });

  const loadEmployees = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    fetchEmployeeManagementList()
      .then((rows) => {
        setEmployees(
          rows.map((row) => ({
            id: row.id,
            empId: row.empId,
            name: row.name,
            registrationDate: row.registrationDate,
            birthDate: row.birthDate,
            email: row.email,
            invitationId: row.invitationId || undefined,
            archiveInvitationId: row.archiveInvitationId || undefined,
            gender: row.gender || undefined,
            finalizedAt: row.finalizedAt || undefined,
            needsFinalize: row.needsFinalize === true,
            status: row.status || undefined,
            hireLifecycle: normalizeLifecycle(row.hireLifecycle),
          }))
        );
      })
      .catch(() => {
        setEmployees([]);
        setLoadError('従業員一覧の読み込みに失敗しました。');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const filteredEmployees = employees.filter((emp) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      emp.empId.toLowerCase().includes(query) || emp.name.toLowerCase().includes(query);
    const matchesGender = !filters.gender || emp.gender === filters.gender;
    const matchesLifecycle = !filters.lifecycle || emp.hireLifecycle === filters.lifecycle;
    return matchesSearch && matchesGender && matchesLifecycle;
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleFilterChange = (filterType: 'period' | 'gender' | 'lifecycle', value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  return (
    <div className="emp-management">
      <h1 className="emp-management__title">従業員</h1>
      <div className="emp-management__controls">
        <form className="emp-management__search" onSubmit={handleSearch}>
          <input
            type="text"
            className="emp-management__search-input"
            placeholder="従業員IDまたは氏名で検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="emp-management__search-btn">
            検索
          </button>
        </form>

        <div className="emp-management__filter-wrapper">
          <button
            className="emp-management__filter-btn"
            onClick={() => setShowFilters(!showFilters)}
            aria-label="Filter options"
          >
            <img src={filterIcon} alt="" className="emp-management__filter-icon" aria-hidden />
          </button>

          {showFilters && (
            <div className="emp-management__filter-dropdown">
              <div className="emp-management__filter-group">
                <label>状態</label>
                <select
                  value={filters.lifecycle}
                  onChange={(e) => handleFilterChange('lifecycle', e.target.value)}
                >
                  <option value="">すべて</option>
                  <option value="active">在職 / 登録完了</option>
                  <option value="resigned">退職</option>
                  <option value="rehire_in_progress">再雇用手続き中</option>
                </select>
              </div>
              <div className="emp-management__filter-group">
                <label>期間</label>
                <select
                  value={filters.period}
                  onChange={(e) => handleFilterChange('period', e.target.value)}
                >
                  <option value="">すべて</option>
                  <option value="yearly">年次</option>
                  <option value="monthly">月次</option>
                </select>
              </div>
              <div className="emp-management__filter-group">
                <label>性別</label>
                <select
                  value={filters.gender}
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                >
                  <option value="">すべて</option>
                  <option value="male">男性</option>
                  <option value="female">女性</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <button
          className="emp-management__new-reg-btn"
          onClick={() => {
            setShowNewRegistrationModal(true);
            if (onNewRegistration) {
              onNewRegistration();
            }
          }}
        >
          <img src={addNewIcon} alt="" className="emp-management__new-reg-icon" aria-hidden />
          <span>新規登録</span>
        </button>
      </div>

      {loadError && (
        <p className="emp-management__error" role="alert">
          {loadError}
        </p>
      )}
      {loading ? (
        <p className="emp-management__loading">読み込み中…</p>
      ) : (
        <EmpDataTable
          employees={filteredEmployees}
          onEmployeeClick={onEmployeeClick}
          onManageClick={setManageEmployee}
        />
      )}

      {manageEmployee && (
        <ManageEmployeeModal
          employeeName={manageEmployee.name}
          employeeEmail={manageEmployee.email}
          initialAccountStatus={manageEmployee.status}
          onClose={() => setManageEmployee(null)}
          onUpdated={loadEmployees}
          onStartRehire={onStartRehire}
        />
      )}

      <NewRegistrationModal
        isOpen={showNewRegistrationModal}
        onClose={() => setShowNewRegistrationModal(false)}
        onSave={() => {
          loadEmployees();
        }}
        onUpdate={() => {}}
        onDelete={() => {}}
      />
    </div>
  );
};

export default EmpManagement;
